import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { add, deleteRecord, fetch, update } from '../services/RatePlansService';
import { RatePlan } from '../types';

export const useRatePlan = (ratePlanId?: string) => {
    const queryClient = useQueryClient();

    const ratePlanQuery = useQuery({
        queryKey: ['rate-plan', ratePlanId],
        queryFn: () => fetch(ratePlanId!),
        enabled: !!ratePlanId
    });

    const createMutation = useMutation({
        mutationFn: (values: RatePlan) => {
            const { created: _created, updated: _updated, ...rest } = values;
            return add(rest);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['rate-plans'] });
        }
    });

    const updateMutation = useMutation({
        mutationFn: (values: RatePlan) => {
            if (!ratePlanId) throw new Error('ratePlanId is required parameter');
            const { created: _created, updated: _updated, ...rest } = values;
            return update(ratePlanId, rest);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['rate-plans'] });
            queryClient.invalidateQueries({ queryKey: ['rate-plan', ratePlanId] });
        }
    });

    const deleteMutation = useMutation({
        mutationFn: () => {
            if (!ratePlanId) throw new Error('ratePlanId is required parameter');
            return deleteRecord(ratePlanId);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['rate-plans'] });
        }
    });

    return {
        record: ratePlanQuery.data,
        loading: ratePlanQuery.isLoading,
        fetchRecord: ratePlanQuery.refetch,
        createRecord: createMutation.mutate,
        updateRecord: updateMutation.mutate,
        deleteRecord: deleteMutation.mutate,
        createMutation,
        updateMutation,
        deleteMutation
    };
};